<template>
  <div class="content operations-page">
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <card class="strpied-tabled-with-hover operation-card">
            <template slot="header">
              <div class="row align-items-center">
                <div class="col-md-8">
                  <h4 class="card-title mb-1">Vos opérations</h4>
                  <p class="card-category mb-1" v-if="operations.length === 1">Opération que vous gérez</p>
                  <p class="card-category mb-1" v-if="operations.length > 1">Liste des {{ operations.length }} opérations que vous gérez</p>
                  <p class="card-category mb-1" v-if="operations.length === 0">Aucune opération active</p>
                  <small class="text-muted">{{ operationsSummary }}</small>
                </div>
                <div class="col-md-4 text-md-right mt-3 mt-md-0">
                  <div class="d-inline-flex flex-column align-items-md-end align-items-start">
                    <b-form-checkbox
                      v-model="displayDone"
                      @change="toggleDisplayDone"
                      size="sm"
                      switch
                    >
                      Afficher les opérations terminées
                    </b-form-checkbox>
                    <b-button size="sm" variant="outline-secondary" @click="refreshData">
                      <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
                      Actualiser
                    </b-button>
                  </div>
                </div>
              </div>
            </template>

            <div class="operations-table-wrapper d-flex flex-column flex-grow-1">
              <div v-if="isLoading" class="py-5 text-center text-muted flex-grow-1 d-flex flex-column justify-content-center">
                <b-spinner label="Chargement"></b-spinner>
                <p class="mt-2 mb-0">Chargement des opérations…</p>
              </div>
              <div v-else-if="!isHost()" class="empty-state text-center p-4">
                <b-icon icon="shield-lock" shift-v="-2" font-scale="2"></b-icon>
                <h5 class="mt-3">Cette section est réservée aux Entreprises Utilisatrices hôtes.</h5>
                <p class="text-muted mb-0">Revenez sur l’accueil ou contactez votre administrateur pour y accéder.</p>
              </div>
              <div v-else class="d-flex flex-column flex-grow-1">
                <b-table
                  v-if="operations.length > 0"
                  class="flex-grow-1"
                  responsive
                  striped
                  hover
                  small
                  :sticky-header="tableBodyHeight"
                  :items="filteredOperations"
                  :fields="columns"
                  head-variant="light"
                >
                  <template #thead-top>
                    <tr>
                      <th></th>
                      <th>
                        <b-form-select
                          size="sm"
                          :options="operationStatusOptions"
                          v-model="operationColumnFilters.status"
                        ></b-form-select>
                      </th>
                      <th>
                        <b-form-input
                          size="sm"
                          v-model="operationColumnFilters.code"
                          placeholder="Filtrer"
                        ></b-form-input>
                      </th>
                      <th>
                        <b-form-select
                          size="sm"
                          :options="operationTypeOptions"
                          v-model="operationColumnFilters.type"
                        ></b-form-select>
                      </th>
                      <th>
                        <b-form-input
                          size="sm"
                          v-model="operationColumnFilters.operationname"
                          placeholder="Filtrer"
                        ></b-form-input>
                      </th>
                      <th>
                        <b-form-input
                          size="sm"
                          v-model="operationColumnFilters.entity"
                          placeholder="Filtrer"
                        ></b-form-input>
                      </th>
                      <th>
                        <b-form-input
                          size="sm"
                          v-model="operationColumnFilters.sitename"
                          placeholder="Filtrer"
                        ></b-form-input>
                      </th>
                      <th>
                        <div class="d-flex flex-column flex-md-row">
                          <b-form-input
                            size="sm"
                            type="date"
                            v-model="operationColumnFilters.startDateStart"
                            class="mb-1 mb-md-0"
                            placeholder="Du"
                          ></b-form-input>
                          <b-form-input
                            v-if="operationColumnFilters.startDateStart"
                            size="sm"
                            type="date"
                            v-model="operationColumnFilters.startDateEnd"
                            class="ml-md-1"
                            placeholder="Au"
                          ></b-form-input>
                        </div>
                      </th>
                      <th>
                        <b-button
                          size="sm"
                          variant="outline-secondary"
                          @click="resetOperationFilters"
                        >
                          Réinitialiser
                        </b-button>
                      </th>
                    </tr>
                  </template>
                  <template #cell(rank)="row">
                    {{ row.index + 1 }}
                  </template>
                  <template #cell(_status)="row">
                    <b-badge
                      :variant="statusClass(row.item._status)"
                      v-b-tooltip.hover="statusLabel(row.item._status)"
                      :class="'text-uppercase'"
                    >
                      {{ statusLabel(row.item._status) }}
                    </b-badge>
                  </template>
                  <template #cell(id)="row">
                    <router-link :to="urlWithRedirect('/ppe_eu/operation_home/'+row.item.id+'?tab=companies')">Ouvrir</router-link>
                  </template>
                  <template #cell(type)="row">
                    {{ typeToText(row.item.type) }}
                  </template>
                  <template #cell(startDate)="row">
                    {{ toDate(row.item.startDate) }}
                  </template>
                  <template #cell(code)="row">
                    <router-link :to="urlWithRedirect('/ppe_eu/operation_home/'+row.item.id)">{{row.item.code}}</router-link>
                  </template>
                </b-table>

                <div v-else class="empty-state text-center p-4 flex-grow-1 d-flex flex-column justify-content-center">
                  <b-icon icon="diagram3" shift-v="-2" font-scale="2"></b-icon>
                  <h5 class="mt-3">Aucune opération n’a encore été créée</h5>
                  <p class="text-muted mb-3">Utilisez le bouton ci-dessous pour décrire votre première opération.</p>
                </div>

                <div class="mt-4" v-if="isPartner()">
                  <b-button variant="success" :to="urlWithRedirect('/ppe_eu/operation/'+account.id)">
                    Décrire une nouvelle opération
                  </b-button>
                </div>
                <h6 v-else class="mt-4">
                  <i>Pour créer une nouvelle opération, faites une demande à APR-2SP ou à la personne qui vous a créé votre compte.</i>
                </h6>
              </div>
            </div>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ppeBusiness from 'ppe-business'
  import swal from 'sweetalert'
  const Business = require('ppe-business')

  import common from '../common/pages.js'

  const OPERATION_FILTERS_DEFAULTS = () => ({
    status: 'all',
    code: '',
    type: 'all',
    operationname: '',
    entity: '',
    sitename: '',
    startDateStart: '',
    startDateEnd: '',
  })

  export default {
    name: 'PPEOperations',
    mounted() {
      this.refreshData()
    },
    data() {
      return {
        account: {},
        company: {},
        operations: [],
        displayDone: false,
        isLoading: false,
        columns: [
          { key: 'rank', label: '#' },
          { key: '_status', label: 'Etat' },
          { key: 'code', label: 'Code' },
          { key: 'type', label: 'Type' },
          { key: 'operationname', label: 'Opération' },
          { key: 'company.name', label: 'Entité' },
          { key: 'sitename', label: 'Site' },
          { key: 'startDate', label: 'Début' },
          { key: 'id', label: 'Action' },
        ],
        operationColumnFilters: OPERATION_FILTERS_DEFAULTS(),
      }
    },
    computed: {
      filteredOperations() {
        const filters = this.operationColumnFilters
        return this.operations.filter(operation => {
          const matchesStatus = filters.status === 'all' || operation._status === filters.status
          const matchesCode = this.matchesText(operation.code, filters.code)
          const matchesType = filters.type === 'all' || operation.type === filters.type
          const matchesName = this.matchesText(operation.operationname, filters.operationname)
          const matchesEntity = this.matchesText(operation.company && operation.company.name, filters.entity)
          const matchesSite = this.matchesText(operation.sitename, filters.sitename)
          const matchesStartDate = this.matchesDate(operation.startDate, filters.startDateStart, filters.startDateEnd)
          return matchesStatus && matchesCode && matchesType && matchesName && matchesEntity && matchesSite && matchesStartDate
        })
      },
      operationStatusOptions() {
        const options = [{ value: 'all', text: 'Tous' }]
        const statuses = Array.from(new Set(this.operations.map(operation => operation._status).filter(Boolean)))
        statuses.forEach(status => options.push({ value: status, text: ppeBusiness.getStatusLabel(status) }))
        return options
      },
      operationTypeOptions() {
        const options = [{ value: 'all', text: 'Tous' }]
        const types = Array.from(new Set(this.operations.map(operation => operation.type).filter(Boolean)))
        types.forEach(type => options.push({ value: type, text: this.typeToText(type) }))
        return options
      },
      operationsSummary() {
        if (!this.operations.length) {
          return 'Aucune opération disponible'
        }
        return `${this.filteredOperations.length} / ${this.operations.length} opération(s) visibles`
      },
      tableBodyHeight() {
        return 'calc(100vh - 260px)'
      }
    },
    watch: {
      'operationColumnFilters.startDateStart'(value) {
        if (!value) {
          this.operationColumnFilters.startDateEnd = ''
        }
      },
      '$route.params.accountid'() {
        this.refreshData()
      }
    },
    methods: {
      ...common.methods,
      refreshData() {
        const url = this.getApiUrl()
        this.isLoading = true
        this.$http.get(url)
          .then(response => {
            this.operations = response.data.operations
            this.company = response.data.company
            this.account = response.data.account
          })
          .catch(err => {
            let error = JSON.parse(JSON.stringify(err))
            swal('Erreur', JSON.stringify(err.response ? err.response.data : error.message), 'error')
          })
          .finally(() => {
            this.isLoading = false
          })
      },
      toggleDisplayDone() {
        this.refreshData()
      },
      toDate(apiDate) {
        return Business.toDate(apiDate)
      },
      typeToText(type) {
        return Business.getOperationLabel(type)
      },
      getApiUrl() {
        let userId
        if (this.$route.params.accountid) userId = this.$route.params.accountid
        else userId = this.$usermanager.getCurrentUser()._id

        return '/eu/dashboardEU?id=' + userId + '&done=' + (this.displayDone ? '1' : '')
      },
      isHost() {
        return this.company && this.company.isHost
      },
      isPartner() {
        return this.account.groups &&
          (this.account.groups.includes(Business.GROUP.PARTNER))
      },
      matchesText(value, filterValue) {
        if (!filterValue) {
          return true
        }
        return (value || '').toString().toLowerCase().includes(filterValue.toLowerCase())
      },
      matchesDate(value, start, end) {
        if (!start && !end) {
          return true
        }
        if (!value) {
          return false
        }
        const currentDate = new Date(value)
        if (Number.isNaN(currentDate.getTime())) {
          return false
        }
        if (start) {
          const startDate = new Date(start)
          startDate.setHours(0, 0, 0, 0)
          if (currentDate < startDate) {
            return false
          }
        }
        if (end) {
          const endDate = new Date(end)
          endDate.setHours(23, 59, 59, 999)
          if (currentDate > endDate) {
            return false
          }
        }
        return true
      },
      resetOperationFilters() {
        Object.assign(this.operationColumnFilters, OPERATION_FILTERS_DEFAULTS())
      },
      /*statusVariant(status) {
        if (!status) return 'secondary'
        const normalized = status.toString().toLowerCase()
        const variants = {
          draft: 'secondary',
          pending: 'warning',
          in_progress: 'info',
          progress: 'info',
          validated: 'success',
          done: 'success',
          completed: 'success',
          archived: 'dark',
          cancelled: 'secondary',
          alert: 'danger'
        }
        return variants[normalized] || 'secondary'
      },*/
      statusLabel(status) {
        if (!status) {
          return 'Non défini'
        }
        return ppeBusiness.getStatusLabel(status)
      },
      statusClass(status) {
        if (!status) {
          return ''
        }
        return ppeBusiness.getStatusClass(status)
      },
      
    }
  }
</script>

<style>
  .operations-page .operation-card {
    min-height: calc(100vh - 230px);
    display: flex;
    flex-direction: column;
  }

  .operations-page .operation-card .card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .operations-table-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .operations-table-wrapper .table-responsive {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .operations-table-wrapper table {
    margin-bottom: 0;
  }

  .operations-table-wrapper table th,
  .operations-table-wrapper table td {
    font-size: 0.85rem;
  }

  .empty-state {
    border-radius: 0.75rem;
    background: #f8f9fc;
  }
</style>
